import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  AgentMessage,
  Message,
  MessageType,
  TaskStatus,
  type ActiveTool,
} from "./AgentMessage";
import { phaseGroupingEnabled } from "./phase-grouping";
import { AgentModeDropdown, ChatMode } from "./AgentModeDropdown";
import { ModelDropdown, ModelType } from "./ModelDropdown";
import { AttachmentDropdown } from "../editor/AttachmentDropdown";
import {
  ArrowUp,
  StopCircle,
  Loader2,
  X,
  FileIcon,
  FileText,
  Upload,
  Bot,
  Eye,
  ArrowDown,
  Mic,
  RefreshCw,
  MoreHorizontal,
  MessageCircle,
} from "lucide-react";
import { MentionDropdown, type MentionSelection } from "./mention-dropdown";
import { MentionBadge } from "./mention-badge";
import { extractUIPayload } from "@/lib/agent/payload-parser";
import { ThinkingIndicator } from "./thinking-indicator";
import { SessionFeedbackPulse } from "./SessionFeedbackPulse";
import { editorAPI } from "@/lib/editor/api";
import { cn } from "@/lib/utils";
import { useAgentInputStore } from "@/stores/agent-input-store";
import { isEnterKey } from "@/lib/utils/keyboard";
import toast from "react-hot-toast";
import { useResponsive } from "@/hooks/useResponsive";
import { useSpeechRecognition } from "@/hooks/use-speech-recognition";
import { CreditsWarning, CreditAlertLevel } from "./CreditsDisplay";
import { CreditDrawer } from "./CreditDrawer";
import type { FreeDailyCredits } from "@/lib/api/credits";
import { PreviewTestReopenTab } from "@/components/preview/preview-test-window";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import type { ThemeContent } from "@/types/theme.types";
import { useThemeEditor } from "@/hooks/use-theme-editor";

interface FileWithState {
  file: File;
  uploadState?: "pending" | "uploading" | "completed" | "failed";
  uploadProgress?: number;
  error?: string;
}

// Stable id so the "image still uploading" toast is deduped and can be
// dismissed once the real upload response lands.
const UPLOAD_TOAST_ID = "image-uploading";

interface AgentChatProps {
  messages: Message[];
  mode: ChatMode;
  model: ModelType;
  onModeChange: (mode: ChatMode) => void;
  onModelChange: (model: ModelType) => void;
  onSendPrompt: (prompt: string) => void;
  onStopChat?: () => void;
  onClearChat?: () => void;
  isStreaming?: boolean;
  disabled?: boolean;
  onSelectAttach?: () => void;
  onSelectConnections?: () => void;
  onSelectProjectSettings?: () => void;
  onSelectImageGenerator?: () => void;
  onSelectMediaLibrary?: () => void;
  onSelectOptisignsAssets?: () => void;
  onSelectThemes?: () => void;
  /** OptiSigns assets picked in the browse panel, to be merged in as @asset chips. */
  pendingAssetRefs?: { assetId: string; name: string }[];
  /** Called once pendingAssetRefs have been merged into the composer, to clear them. */
  onPendingAssetRefsConsumed?: () => void;
  attachedFile?: File | null;
  attachedFiles?: FileWithState[];
  onRemoveAttachment?: (fileName?: string) => void;
  onFilesDropped?: (files: File[]) => void;
  onOpenFile?: (filePath: string) => void;
  onAction?: (actionId: string, messageId: string) => void;
  onOpenVisualEditor?: () => void;
  onOpenHistory?: () => void;
  devContainerId?: string;
  projectId?: string;
  sessionHeaderActions?: React.ReactNode;
  mobileActiveTab?: string;
  onMobileActiveTabChange?: (tab: string) => void;
  onOpenSettings?: () => void;
  creditBalance?: number | null;
  allocatedCredits?: number | null;
  /** Free-tier daily model state (standalone free only); drives the credit drawer. */
  freeDaily?: FreeDailyCredits | null;
  /** Open the upgrade modal (credits context). Fired on send when out of credits. */
  onOpenUpgrade?: () => void;
  isLoadingCredits?: boolean;
  shownCreditAlerts?: Set<CreditAlertLevel>;
  onCreditAlertShown?: (level: CreditAlertLevel) => void;
  byocConnected?: boolean;
  byocEnabled?: boolean;
  isEmailVerificationBlocked?: boolean;
  onCheckEmailVerification?: () => Promise<void>;
  isCheckingEmailVerification?: boolean;
  onResendVerificationEmail?: () => Promise<void>;
  isResendingVerificationEmail?: boolean;
  isContainerReady?: boolean;
  // Connection lost banner
  connectionLost?: boolean;
  onConnectionLostDismiss?: () => void;
  // Theme preview props (required for ThemePreviewCard in ClaudeResponseCard)
  workspaceId: string;
  // Active claude session id (for the session-feedback pulse)
  sessionId?: string | null;
  previewIframe: HTMLIFrameElement | null;
  previewUrl?: string;
  onEditTheme?: (theme: ThemeContent) => void;
  onThemeSaved?: (themeId: string) => void;
  onBrowseThemes?: () => void;
  onConnectionCreated?: (
    connectionId: string,
    connectorType: string,
    connectionName: string,
  ) => void;
  onCacheTtlSet?: (path: string, ttl: number) => void;
}

// ============================================================
// Turn merge (for phase grouping)
// ============================================================
//
// A turn is stored as ONE message while streaming (tools accumulate together)
// but `getMessagesFromEntries` splits it into one-message-per-text-block on
// reload. Phase grouping needs the whole turn, so we merge a turn's consecutive
// "work" messages back into one — re-basing each tool's textPosition into the
// combined content so inline tool placement is preserved. This makes the
// reloaded shape identical to the streaming shape, so phases render the same
// live and after refresh. HITL cards, activation cards, and the loading
// placeholder are never merged (they keep their own rendering + action state).

const TURN_MERGE_SEP = "\n\n";

/** An assistant message that should fold into its turn's combined work message. */
function isMergeableWork(m: Message): boolean {
  return (
    m.type === MessageType.ASSISTANT &&
    !!m.userMessageId &&
    !m.isLoading &&
    !m.activation &&
    !(m.content || "").includes("<ui_payload>")
  );
}

/** Combine a run of work messages into one, offsetting tool textPositions. */
function mergeWorkMessages(group: Message[]): Message {
  if (group.length === 1) return group[0];

  let content = "";
  const activeTools: ActiveTool[] = [];
  let toolUsage: Message["toolUsage"] | undefined;

  for (const m of group) {
    const offset = content.length ? content.length + TURN_MERGE_SEP.length : 0;
    const localLen = (m.content || "").length;
    (m.activeTools || []).forEach((t) => {
      activeTools.push({ ...t, textPosition: offset + (t.textPosition ?? localLen) });
    });
    content = content.length
      ? content + TURN_MERGE_SEP + (m.content || "")
      : m.content || "";
    if (m.toolUsage) {
      toolUsage = toolUsage || {};
      (["Read", "Write", "Edit"] as const).forEach((k) => {
        const vals = m.toolUsage?.[k];
        if (vals?.length) toolUsage![k] = [...(toolUsage![k] || []), ...vals];
      });
    }
  }

  const first = group[0];
  const last = group[group.length - 1];
  const anyRunning = group.some((m) => m.status === TaskStatus.RUNNING);
  return {
    ...first,
    content,
    activeTools: activeTools.length ? activeTools : undefined,
    toolUsage,
    status: anyRunning ? TaskStatus.RUNNING : last.status,
  };
}

/** Merge each turn's consecutive work messages so phase grouping sees the turn. */
function mergeTurnsForPhases(ordered: Message[]): Message[] {
  const out: Message[] = [];
  let i = 0;
  while (i < ordered.length) {
    const msg = ordered[i];
    if (!isMergeableWork(msg)) {
      out.push(msg);
      i++;
      continue;
    }
    const turnId = msg.userMessageId;
    const group: Message[] = [];
    let j = i;
    while (
      j < ordered.length &&
      isMergeableWork(ordered[j]) &&
      ordered[j].userMessageId === turnId
    ) {
      group.push(ordered[j]);
      j++;
    }
    out.push(mergeWorkMessages(group));
    i = j;
  }
  return out;
}

export function AgentChat({
  messages,
  mode,
  model,
  onModeChange,
  onModelChange,
  onSendPrompt,
  onStopChat,
  onClearChat,
  isStreaming = false,
  disabled = false,
  onSelectAttach,
  onSelectConnections,
  onSelectProjectSettings,
  onSelectImageGenerator,
  onSelectMediaLibrary,
  onSelectOptisignsAssets,
  onSelectThemes,
  pendingAssetRefs,
  onPendingAssetRefsConsumed,
  attachedFile,
  attachedFiles = [],
  onRemoveAttachment,
  onFilesDropped,
  onOpenFile,
  onAction,
  onOpenVisualEditor,
  onOpenHistory,
  devContainerId,
  projectId,
  sessionHeaderActions,
  mobileActiveTab,
  onMobileActiveTabChange,
  onOpenSettings,
  creditBalance = null,
  allocatedCredits = null,
  freeDaily = null,
  onOpenUpgrade,
  isLoadingCredits = false,
  shownCreditAlerts,
  onCreditAlertShown,
  byocConnected = false,
  byocEnabled = true,
  isEmailVerificationBlocked = false,
  onCheckEmailVerification,
  isCheckingEmailVerification = false,
  onResendVerificationEmail,
  isResendingVerificationEmail = false,
  isContainerReady = true,
  connectionLost = false,
  onConnectionLostDismiss,
  workspaceId,
  sessionId,
  previewIframe,
  previewUrl,
  onEditTheme,
  onThemeSaved,
  onBrowseThemes,
  onConnectionCreated,
  onCacheTtlSet,
}: AgentChatProps) {
  const navigate = useNavigate();
  const { canUseAgent, isViewer } = usePermissions();

  // Check if credits are insufficient (balance <= 0)
  // BYOC users bypass credit checks - they pay Anthropic directly
  const numBalance =
    creditBalance !== null
      ? typeof creditBalance === "string"
        ? parseFloat(creditBalance as string)
        : creditBalance
      : null;
  const isInsufficientCredits =
    !byocConnected && numBalance !== null && numBalance <= 0;

  // Check if chat should be blocked (email not verified OR viewer role).
  // NOTE: insufficient credits deliberately does NOT block the composer — free users
  // can still type; send opens the upgrade modal (see handleSubmit + CreditDrawer).
  const isChatBlocked = isEmailVerificationBlocked || !canUseAgent;

  // Use attachedFiles if provided, otherwise fall back to single attachedFile for backward compatibility
  const files: FileWithState[] =
    attachedFiles.length > 0
      ? attachedFiles
      : attachedFile
        ? [{ file: attachedFile, uploadState: "completed" }]
        : [];
  // True while any attachment is still uploading to R2 — blocks submit so a
  // message can't be sent with an incomplete attachment (broken image link).
  const hasPendingUpload = files.some(
    (f) => f.uploadState === "uploading" || f.uploadState === "pending",
  );
  const [prompt, setPrompt] = useState("");
  // Tracks a user-closed credit drawer so the Preview-Test reopen tab (suppressed
  // while the drawer shows, so the drawer stays the top-most notice) can return.
  const [creditDrawerDismissed, setCreditDrawerDismissed] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // @ mention state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(
    null,
  );
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  // Cross-project referencing state
  const [referenceableProjects, setReferenceableProjects] = useState<
    {
      id: string;
      name: string;
      description: string | null;
      mode: string;
      devContainerStatus: string | null;
    }[]
  >([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [mentionFiles, setMentionFiles] = useState<string[]>([]);
  const [mentionChips, setMentionChips] = useState<
    {
      type: "project" | "file" | "theme" | "skill" | "asset";
      name: string;
      id?: string;
    }[]
  >([]);
  const [filePreviews, setFilePreviews] = useState<Map<string, string>>(
    new Map(),
  );

  // Merge assets picked in the OptiSigns browse panel into the composer as
  // @asset chips (dedup by assetId), then clear the pending buffer.
  useEffect(() => {
    if (!pendingAssetRefs || pendingAssetRefs.length === 0) return;
    setMentionChips((prev) => {
      const existing = new Set(prev.map((c) => `${c.type}:${c.id || c.name}`));
      const additions = pendingAssetRefs
        .filter((r) => !existing.has(`asset:${r.assetId}`))
        .map((r) => ({ type: "asset" as const, name: r.name, id: r.assetId }));
      return additions.length ? [...prev, ...additions] : prev;
    });
    onPendingAssetRefsConsumed?.();
  }, [pendingAssetRefs, onPendingAssetRefsConsumed]);
  const [isDragging, setIsDragging] = useState(false);
  const mentionConfirmRef = useRef<(() => void) | null>(null);
  const mentionSwitchTabRef = useRef<
    ((direction: "left" | "right") => void) | null
  >(null);
  const mentionItemCountRef = useRef<number>(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const userScrolledDuringStream = useRef(false);
  // --- Anchored send ("pin the sent message to the top") ---------------------
  // On send we lift the just-sent user message to the top of the view and open
  // a screen of room below it, then let the reply stream into that space —
  // instead of chasing the scroll to the bottom. `anchorModeRef` marks a turn
  // as anchored; `anchorSpacer` is the room we open (a spacer at the end of the
  // list) and gets trimmed once the turn finishes so there's no dead scroll.
  const anchorModeRef = useRef(false);
  const anchorSpacerRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotionRef = useRef(false);
  const prevStreamingRef = useRef(false);
  const [anchorSpacer, setAnchorSpacer] = useState(0);
  const ANCHOR_TOP_PADDING = 12;
  // True once the "image still uploading" toast has been shown (user tried to
  // submit mid-upload). Lets the upload-watch effect resolve that exact toast
  // when the real upload response lands, instead of a hard-coded timeout.
  const uploadToastActiveRef = useRef(false);
  // Tracks the chat panel's own width (it's resizable) so the toolbar can
  // collapse the mode/model labels to icons when there isn't room for them.
  const rootContainerRef = useRef<HTMLDivElement>(null);
  const [panelWidth, setPanelWidth] = useState(0);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  // Use external state if provided, otherwise use internal state
  const [internalMobileActiveTab, setInternalMobileActiveTab] =
    useState("chat");
  const currentActiveTab =
    mobileActiveTab !== undefined ? mobileActiveTab : internalMobileActiveTab;
  const { isMobile } = useResponsive();

  // Observe the chat panel width (resizable divider) for responsive toolbar layout
  useEffect(() => {
    const el = rootContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      setPanelWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Below this width the mode/model labels don't fit alongside the other controls,
  // so collapse them to icon-only (full names remain available via tooltip).
  const isCompactToolbar = panelWidth > 0 && panelWidth < 360;

  // Voice recognition hook
  const {
    isRecording,
    isProcessing: isProcessingVoice,
    startRecording,
    stopRecording,
  } = useSpeechRecognition({
    onResult: (transcript) => {
      setPrompt(transcript);
    },
  });

  // Generic rotating status messages (30+)

  // Handle voice recording toggle
  const handleVoiceToggle = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      setPrompt(""); // Clear previous chat input
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Mirror div that renders URLs as links behind the transparent textarea.
  // Kept scroll-synced to the textarea (see onScroll below) so the links stay
  // aligned with the caret once the input grows past its max height.
  const mirrorRef = useRef<HTMLDivElement>(null);
  const dragCounter = useRef(0);
  const lastEscTimeRef = useRef<number>(0);
  const ESC_TIMEOUT = 500; // 500ms timeout for double ESC

  // External chip injection (e.g. slide thumbnail's "Edit with Agent" action).
  // Subscribes to `pendingChips` and drains them into local state whenever a
  // sibling component pushes — not consume-on-mount, so runtime injections
  // after the chat is already open work the same as injections that triggered
  // the panel to open in the first place. See stores/agent-input-store.ts.
  const pendingChips = useAgentInputStore((s) => s.pendingChips);
  const drainChips = useAgentInputStore((s) => s.drainChips);
  useEffect(() => {
    if (pendingChips.length === 0) return;
    const incoming = drainChips();
    setMentionChips((prev) => {
      const seen = new Set(prev.map((c) => `${c.type}:${c.name}`));
      const merged = [
        ...prev,
        ...incoming.filter((c) => !seen.has(`${c.type}:${c.name}`)),
      ];
      return merged;
    });
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [pendingChips, drainChips]);

  // Generate preview URLs for image files
  useEffect(() => {
    // Cleanup old URLs
    filePreviews.forEach((url) => URL.revokeObjectURL(url));

    const newPreviews = new Map<string, string>();

    files.forEach((fileWithState) => {
      if (fileWithState.file.type?.startsWith("image/")) {
        const url = URL.createObjectURL(fileWithState.file);
        newPreviews.set(fileWithState.file.name, url);
      }
    });

    setFilePreviews(newPreviews);

    // Cleanup URLs when component unmounts
    return () => {
      newPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.length]);

  // Track the OS "reduce motion" preference so anchored scrolls jump instantly
  // for users who opt out of animation.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    prefersReducedMotionRef.current = mq.matches;
    const onChange = () => {
      prefersReducedMotionRef.current = mq.matches;
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // A real scroll intent (wheel/touch) during an anchored turn means the user
  // wants to read at their own pace — stop re-pinning to the top. Programmatic
  // scrolls don't fire these, so this cleanly separates user intent from ours.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const onIntent = () => {
      if (anchorModeRef.current) userScrolledDuringStream.current = true;
    };
    container.addEventListener("wheel", onIntent, { passive: true });
    container.addEventListener("touchmove", onIntent, { passive: true });
    return () => {
      container.removeEventListener("wheel", onIntent);
      container.removeEventListener("touchmove", onIntent);
    };
  }, [currentActiveTab]);

  // Scroll the most recent user message flush to the top of the view.
  const anchorLastUserToTop = (smooth: boolean) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const nodes = container.querySelectorAll<HTMLElement>(
      '[data-message-role="user"]',
    );
    const last = nodes[nodes.length - 1];
    if (!last) return;
    const containerRect = container.getBoundingClientRect();
    const msgRect = last.getBoundingClientRect();
    const target =
      container.scrollTop +
      (msgRect.top - containerRect.top) -
      ANCHOR_TOP_PADDING;
    container.scrollTo({
      top: Math.max(0, target),
      behavior: smooth ? "smooth" : "instant",
    });
  };

  // Begin an anchored turn (called from both send paths). Opens a full screen
  // of room below the sent message so it can rise to the top even when the
  // reply is short; the message effect performs the actual pin once the new
  // message is in the DOM.
  const beginAnchoredTurn = () => {
    anchorModeRef.current = true;
    userScrolledDuringStream.current = false;
    const container = messagesContainerRef.current;
    if (container) setAnchorSpacer(container.clientHeight);
  };

  // Leave anchored mode and drop the room we opened (user caught up / switched
  // context). Safe to call when not anchored.
  const exitAnchoredTurn = () => {
    if (!anchorModeRef.current && anchorSpacer === 0) return;
    anchorModeRef.current = false;
    userScrolledDuringStream.current = false;
    setAnchorSpacer(0);
  };

  // Reset anchoring when the active session actually switches. Crucially, do
  // NOT reset while an anchored turn is in flight: a brand-new conversation is
  // assigned its real session id mid-stream, and that assignment must not tear
  // down the anchor. `anchorModeRef` is set synchronously on send, so it is a
  // reliable "we're mid-turn" signal (unlike `isStreaming`, which the parent
  // flips a tick later than `sessionId`).
  useEffect(() => {
    if (anchorModeRef.current) return;
    setAnchorSpacer(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Auto-scroll to bottom when new messages arrive
  // Skip auto-scroll only if: streaming is active AND user has scrolled up
  useEffect(() => {
    if (!messagesContainerRef.current) return;

    const container = messagesContainerRef.current;

    // Anchored turn: keep the just-sent message pinned to the top and let the
    // reply stream into the room below, rather than chasing the bottom. Skip
    // once the user scrolls away to read at their own pace.
    if (anchorModeRef.current) {
      if (!userScrolledDuringStream.current) {
        const smooth = !prefersReducedMotionRef.current;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => anchorLastUserToTop(smooth)),
        );
      }
      return;
    }

    const scrollToBottom = (smooth = true) => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: smooth ? "smooth" : "instant",
        });
      }
    };

    // Check if we should skip scroll (user scrolled up during streaming)
    const shouldSkipScroll = () =>
      isStreaming && userScrolledDuringStream.current;

    // Initial scroll when messages change (unless user scrolled up during streaming)
    if (!shouldSkipScroll()) {
      scrollToBottom(false);
    }

    // Use MutationObserver to scroll after DOM changes settle
    let scrollTimeout: NodeJS.Timeout;
    const observer = new MutationObserver(() => {
      // Check INSIDE the callback - user may have scrolled up since observer was set up
      if (shouldSkipScroll()) return;

      // Debounce: scroll after DOM stops changing for 50ms
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (!shouldSkipScroll()) {
          scrollToBottom(true);
        }
      }, 50);
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Also scroll after a max delay as fallback
    const maxTimeout = setTimeout(() => {
      if (!shouldSkipScroll()) {
        scrollToBottom(true);
      }
      observer.disconnect();
    }, 300);

    return () => {
      clearTimeout(scrollTimeout);
      clearTimeout(maxTimeout);
      observer.disconnect();
    };
  }, [messages, isStreaming]);

  // Auto-scroll when streaming starts (to show loading indicator)
  useEffect(() => {
    const wasStreaming = prevStreamingRef.current;
    prevStreamingRef.current = isStreaming;

    // Turn finished during an anchored turn: trim the room we opened down to
    // just enough that the anchored turn can stay put — no dead scroll beneath
    // a long reply, no upward jump beneath a short one.
    if (wasStreaming && !isStreaming && anchorModeRef.current) {
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current;
        const spacer = anchorSpacerRef.current;
        const nodes = container?.querySelectorAll<HTMLElement>(
          '[data-message-role="user"]',
        );
        const lastUser = nodes && nodes[nodes.length - 1];
        if (!container || !spacer || !lastUser) {
          setAnchorSpacer(0);
          return;
        }
        const turnHeight =
          spacer.getBoundingClientRect().top -
          lastUser.getBoundingClientRect().top;
        const room = Math.max(
          0,
          container.clientHeight - turnHeight - ANCHOR_TOP_PADDING,
        );
        setAnchorSpacer(room);
      });
      return;
    }

    if (isStreaming && messagesContainerRef.current) {
      // Anchored turns manage their own scroll (pin-to-top) — don't yank to the
      // bottom here.
      if (anchorModeRef.current) return;

      userScrolledDuringStream.current = false; // Reset on new stream

      const scrollToBottom = () => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      };

      // Scroll immediately and after short delay
      scrollToBottom();
      const timeoutId = setTimeout(scrollToBottom, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isStreaming]);

  // Detect when user scrolls during streaming
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;

    const container = messagesContainerRef.current;
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      100;

    // Anchored turn: show the jump-to-bottom affordance, and hand back to
    // normal bottom-following once the USER scrolls all the way down. Gate on
    // userScrolledDuringStream (set only by real wheel/touch) so our own anchor
    // scroll — which legitimately lands "near the bottom" when the reply is
    // shorter than a screen — doesn't trip the exit and rip out the spacer.
    if (anchorModeRef.current) {
      setShowScrollToBottom(!isNearBottom);
      if (isNearBottom && userScrolledDuringStream.current) exitAnchoredTurn();
      return;
    }

    if (isStreaming) {
      if (!isNearBottom) {
        // User scrolled up during streaming - stop auto-scrolling, show button
        userScrolledDuringStream.current = true;
        setShowScrollToBottom(true);
      } else {
        // At bottom - resume auto-scrolling, hide button
        userScrolledDuringStream.current = false;
        setShowScrollToBottom(false);
      }
    } else {
      // Not streaming - just manage button visibility based on position
      setShowScrollToBottom(!isNearBottom && messages.length > 0);
    }
  };

  // Scroll to bottom handler (for button click)
  const handleScrollToBottom = () => {
    if (messagesContainerRef.current) {
      // User explicitly wants the bottom — leave the anchored turn behind.
      exitAnchoredTurn();
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
      setShowScrollToBottom(false);
    }
  };

  // Handle paste events for images
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            // Rename pasted images to "pasted-{timestamp}-{index}.ext" for uniqueness
            // Backend will add refId, final name will be: refId-pasted-{timestamp}-{index}.png
            const extension = file.type.split("/")[1] || "png";
            const timestamp = Date.now();
            const newName = `pasted-${timestamp}-${i}.${extension}`;
            const renamedFile = new File([file], newName, { type: file.type });
            files.push(renamedFile);
          }
        }
      }

      if (files.length > 0 && onFilesDropped) {
        e.preventDefault();
        onFilesDropped(files);
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, [onFilesDropped]);

  // Dismiss the "image still uploading" toast when the real upload resolves
  // (completed, failed, or removed) — not on a fixed timeout. Only acts when the
  // user actually tried to submit mid-upload (uploadToastActiveRef). On failure,
  // useFileUpload shows its own error toast.
  useEffect(() => {
    if (hasPendingUpload || !uploadToastActiveRef.current) return;
    uploadToastActiveRef.current = false;
    toast.dismiss(UPLOAD_TOAST_ID);
  }, [hasPendingUpload]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Block submit while a pasted/attached image is still uploading to R2.
    // Mirrors the send button's disabled guard (see line ~1728) so the Enter-key
    // path can't send a message with an incomplete attachment (broken image link).
    // The toast persists (no fixed duration) and is resolved by the upload-watch
    // effect when the actual upload response lands.
    if (hasPendingUpload) {
      toast("Image still uploading…", {
        id: UPLOAD_TOAST_ID,
        icon: <Loader2 className="h-4 w-4 animate-spin text-primary" />,
        duration: Infinity,
      });
      uploadToastActiveRef.current = true;
      return;
    }

    // Out of credits (free daily wall) → don't send, open the upgrade modal instead.
    // Composer is never blocked: the typed prompt stays in the box so it's ready
    // to send once the user upgrades. BYOC users pay Anthropic directly, so skip.
    if (prompt.trim() && isInsufficientCredits && !byocConnected) {
      onOpenUpgrade?.();
      return;
    }

    // Allow submission only when not disabled, not streaming, and container is ready
    if (prompt.trim() && !disabled && !isStreaming && isContainerReady) {
      // Build mention prefix from chips
      const mentionPrefix = mentionChips
        .map((c) => {
          if (c.type === "project") return `@project:${c.name}[${c.id}]`;
          if (c.type === "file") return `@file:${c.name}`;
          if (c.type === "skill") return `@skill:${c.id || c.name}`;
          if (c.type === "asset") return `@asset:${c.id}[${c.name}]`;
          return `@theme:${c.name}`;
        })
        .join(" ");
      const fullPrompt = mentionPrefix
        ? `${mentionPrefix} ${prompt.trim()}`
        : prompt.trim();
      onSendPrompt(fullPrompt);
      setPrompt("");
      setMentionChips([]);
      // Reset textarea height to original size (min-h-[44px] from CSS will take over)
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
      // Pin the just-sent message to the top and open room for the reply.
      beginAnchoredTurn();
      // Close mention dropdown
      setShowMentionDropdown(false);
    }
  };

  // Load current project files for @ mention Files tab (once on dropdown open)
  useEffect(() => {
    if (!showMentionDropdown || !projectId || mentionFiles.length > 0) return;
    editorAPI
      .listProjectFiles(projectId)
      .then((files) => {
        if (Array.isArray(files)) setMentionFiles(files);
      })
      .catch(() => {});
  }, [showMentionDropdown, projectId, mentionFiles.length]);

  // Fetch referenceable projects when mention dropdown opens
  useEffect(() => {
    if (!showMentionDropdown || !workspaceId) return;
    setIsLoadingProjects(true);
    editorAPI
      .searchReferenceableProjects(
        mentionQuery || undefined,
        projectId || undefined,
      )
      .then((data) => {
        if (Array.isArray(data)) setReferenceableProjects(data);
      })
      .catch(() => {})
      .finally(() => setIsLoadingProjects(false));
  }, [showMentionDropdown, mentionQuery, workspaceId, projectId]);

  // Theme editor hook for @ mentions (available in all sessions)
  const { themes, isLoading, currentTheme } = useThemeEditor({
    workspaceId,
    previewIframe,
    projectId,
  });

  // Handle mention selection — all types become chips (no text in textarea)
  const handleMentionSelect = useCallback(
    (selection: MentionSelection): void => {
      if (mentionStartIndex === null) return;

      // Remove the @ that triggered the dropdown
      const before = prompt.substring(0, mentionStartIndex);
      const after = prompt.substring(
        mentionStartIndex + 1 + mentionQuery.length,
      );
      setPrompt(`${before}${after}`);

      // Add as chip (deduplicate)
      setMentionChips((prev) => {
        const key = `${selection.type}:${selection.id || selection.name}`;
        if (prev.some((c) => `${c.type}:${c.id || c.name}` === key))
          return prev;
        return [
          ...prev,
          { type: selection.type, name: selection.name, id: selection.id },
        ];
      });

      setShowMentionDropdown(false);
      setMentionQuery("");
      setMentionStartIndex(null);
      setSelectedMentionIndex(0);

      textareaRef.current?.focus();
    },
    [mentionStartIndex, mentionQuery, prompt],
  );

  // Remove a mention chip
  const handleRemoveMentionChip = useCallback((index: number) => {
    setMentionChips((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Split the prompt into text + URL segments so the mirror behind the
  // textarea can render URLs as styled <a>-like spans (link-blue + underline).
  // Plain textareas can't render inline elements, so we layer:
  //   - a styled mirror div (visible, displays URLs as links)
  //   - a transparent-text textarea on top (handles input, caret, selection)
  // Pixel-perfect alignment requires identical font / size / line-height / padding.
  const promptSegments = useMemo(() => {
    const URL_RE = /https?:\/\/[^\s]+/g;
    const segments: Array<{ type: "text" | "url"; value: string }> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = URL_RE.exec(prompt)) !== null) {
      if (match.index > lastIndex) {
        segments.push({
          type: "text",
          value: prompt.slice(lastIndex, match.index),
        });
      }
      segments.push({ type: "url", value: match[0] });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < prompt.length) {
      segments.push({ type: "text", value: prompt.slice(lastIndex) });
    }
    return segments;
  }, [prompt]);

  const hasUrl = promptSegments.some((s) => s.type === "url");

  // Insert @ at cursor and open mention dropdown (triggered by "Add reference" button)
  const handleAddReference = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const before = prompt.substring(0, cursorPos);
    const after = prompt.substring(cursorPos);
    const needsSpace =
      before.length > 0 && !before.endsWith(" ") && !before.endsWith("\n");
    const newText = before + (needsSpace ? " @" : "@") + after;
    const newCursorPos = cursorPos + (needsSpace ? 2 : 1);

    setPrompt(newText);
    setMentionQuery("");
    setMentionStartIndex(newCursorPos - 1);
    setShowMentionDropdown(true);
    setSelectedMentionIndex(0);

    // Focus and set cursor after the @
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    });
  }, [prompt]);

  // Handle prompt change with @ mention detection
  const handlePromptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      const value = e.target.value;
      const cursorPos = e.target.selectionStart;
      setPrompt(value);

      // Auto-resize textarea
      const textarea = e.target;
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;

      // Handle @ mentions for themes (available in all sessions)
      // Find @ symbol before cursor
      const textBeforeCursor = value.substring(0, cursorPos);
      const lastAtIndex = textBeforeCursor.lastIndexOf("@");

      if (lastAtIndex !== -1) {
        // Check if @ is at start or preceded by whitespace
        const charBeforeAt =
          lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : " ";
        if (
          charBeforeAt === " " ||
          charBeforeAt === "\n" ||
          lastAtIndex === 0
        ) {
          const query = textBeforeCursor.substring(lastAtIndex + 1);
          // Only show dropdown if query doesn't contain whitespace (user is still typing the mention)
          if (!query.includes(" ") && !query.includes("\n")) {
            setMentionQuery(query);
            setMentionStartIndex(lastAtIndex);
            setShowMentionDropdown(true);
            setSelectedMentionIndex(0);
            return;
          }
        }
      }

      // Close dropdown if no valid @ pattern
      setShowMentionDropdown(false);
    },
    [],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Backspace on empty input removes last mention chip
    if (
      (e.key === "Backspace" || e.key === "Delete") &&
      prompt === "" &&
      mentionChips.length > 0 &&
      !showMentionDropdown
    ) {
      e.preventDefault();
      setMentionChips((prev) => prev.slice(0, -1));
      return;
    }

    // Handle mention dropdown navigation
    if (showMentionDropdown) {
      if (e.key === "Escape") {
        e.preventDefault();
        setShowMentionDropdown(false);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const max = mentionItemCountRef.current - 1;
        setSelectedMentionIndex((prev) => Math.min(prev + 1, Math.max(0, max)));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedMentionIndex((prev) => Math.max(0, prev - 1));
        return;
      }
      // Enter/Tab to select highlighted item
      // isEnterKey prevents double-trigger during IME composition (Vietnamese, CJK)
      if (isEnterKey(e) || e.key === "Tab") {
        e.preventDefault();
        mentionConfirmRef.current?.();
        return;
      }
      // Left/Right arrows to switch tabs
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        mentionSwitchTabRef.current?.("left");
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        mentionSwitchTabRef.current?.("right");
        return;
      }
    }

    // Handle Enter key for submission (desktop only)
    // On mobile, Enter creates a new line; user taps send button to submit
    // isEnterKey prevents double-trigger during IME composition (Vietnamese, CJK)
    if (isEnterKey(e) && !e.shiftKey && !isMobile) {
      e.preventDefault();
      handleSubmit(e);
      return;
    }

    // Handle double ESC to clear chat input (similar to Claude Code)
    if (e.key === "Escape") {
      const now = Date.now();
      const timeSinceLastEsc = now - lastEscTimeRef.current;

      if (timeSinceLastEsc < ESC_TIMEOUT) {
        // Double ESC detected - clear the input
        e.preventDefault();
        setPrompt("");
        // Reset textarea height to original size
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
        }
        lastEscTimeRef.current = 0; // Reset
      } else {
        // First ESC - record the time
        lastEscTimeRef.current = now;
      }
    } else {
      // Reset ESC timer if any other key is pressed
      lastEscTimeRef.current = 0;
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0 && onFilesDropped) {
      const filesArray = Array.from(droppedFiles);
      onFilesDropped(filesArray);
    }
  };

  // Get mode-specific helper text
  const getModeHelperText = () => {
    switch (mode) {
      case "plan":
        return "Analyze and plan";
      case "execute":
        return "Write and modify";
      default:
        return "Ask Claude";
    }
  };

  // Memoize message reordering to avoid O(n²) computation on every render
  const reorderedMessages = useMemo(() => {
    // Pre-step: anchor stateless tool-RESULT HITL cards inline beneath their
    // producing tool. The sidecar delivers these cards as a SEPARATE ui_payload
    // message, which would otherwise render at the bottom of the turn — after the
    // agent's concluding text. Instead we attach the payload to the producing tool
    // in the same turn and drop the standalone message, so AgentMessage renders the
    // card directly under that tool. These cards carry no per-message action state
    // (clickedActions), so dropping the message is safe. Works identically live
    // (one text+tools message) and on reload (tools split across text entries).
    // Falls back to standalone if no producing tool is found.
    //
    // Decision-gate cards (approve_plan, publish_project, activate_optidev_cloud,
    // setup_connection, cross-project*) are intentionally NOT listed: they use the
    // clickedActions mechanism (message-scoped, cross-tab synced, stale-detected)
    // and their button belongs after the explanatory content — so they keep their
    // trailing placement.
    //
    // hitl_type → producing tool name substrings. MCP tool names arrive prefixed
    // (e.g. mcp__0__generate_theme_hitl), so substring matching is correct.
    const INLINE_HITL_TOOLS: Record<string, string[]> = {
      theme_approval: ["generate_theme_hitl", "generate_theme"],
      theme_gallery: ["list_themes_hitl", "list_themes"],
      media_library_results: [
        "optidev_search_media",
        "optidev_download_media",
        "search_media",
      ],
      optisigns_asset_results: [
        "list_optisigns_assets",
        "list_optisigns_folders",
      ],
      custom_domain: ["optidev_add_custom_domain", "add_custom_domain"],
    };
    const relocatedIds = new Set<string>();
    const working: Message[] = messages.map((m) => m);

    messages.forEach((hitlMsg) => {
      if (hitlMsg.type !== MessageType.ASSISTANT) return;
      if (!hitlMsg.content.includes("<ui_payload>")) return;
      const hitlType = extractUIPayload(hitlMsg.content).payload?.hitl_type;
      const toolSubstrings = hitlType ? INLINE_HITL_TOOLS[hitlType] : undefined;
      if (!toolSubstrings) return; // not inline-able → leave standalone

      // Find the producing tool: the last matching tool in the same turn.
      const turnId = hitlMsg.userMessageId;
      let targetIdx = -1;
      let targetToolIdx = -1;
      working.forEach((m, mi) => {
        if (m.id === hitlMsg.id || !m.activeTools?.length) return;
        if (turnId && m.userMessageId && m.userMessageId !== turnId) return;
        m.activeTools.forEach((t, ti) => {
          const name = t.toolName?.toLowerCase() || "";
          if (toolSubstrings.some((s) => name.includes(s))) {
            targetIdx = mi;
            targetToolIdx = ti;
          }
        });
      });
      if (targetIdx < 0) return; // no producing tool → leave card standalone

      const tm = working[targetIdx];
      const newTools = tm.activeTools!.map((t, ti) =>
        ti === targetToolIdx
          ? {
              ...t,
              hitlPayloadContent: hitlMsg.content,
              hitlMessageId: hitlMsg.id,
              hitlMessageTimestamp: hitlMsg.timestamp,
            }
          : t,
      );
      working[targetIdx] = { ...tm, activeTools: newTools };
      relocatedIds.add(hitlMsg.id);
    });

    // HITL de-duplication (drops into droppedHitlIds). Two layers:
    //  (1) GENERIC safety net — collapse byte-identical HITL cards in the same
    //      turn. The worker already dedups at the source; this guarantees the
    //      user never sees two identical cards even from a cross-task/race
    //      straggler, for ALL hitl_types, live and on reload. Keyed by
    //      (turn + payload), so different cards and later-turn repeats are kept.
    //  (2) ASK supersession — a re-asked optidev_ask_user_question (different
    //      questions) supersedes the prior UNANSWERED ask card; only the newest
    //      stays open. Answered cards always remain (history).
    const droppedHitlIds = new Set<string>();
    const seenHitlSig = new Set<string>();
    const unansweredAskIds: string[] = [];
    working.forEach((m) => {
      if (m.type !== MessageType.ASSISTANT) return;
      if (!m.content.includes("<ui_payload>")) return;

      const sig = `${m.userMessageId || ""}::${m.content}`;
      if (seenHitlSig.has(sig)) {
        droppedHitlIds.add(m.id); // exact duplicate within the turn
        return;
      }
      seenHitlSig.add(sig);

      if (
        extractUIPayload(m.content).payload?.hitl_type === "ask_user_question"
      ) {
        const answered = (m.clickedActions || []).some((a) =>
          a.startsWith("ask_question_answer:"),
        );
        if (!answered) unansweredAskIds.push(m.id);
      }
    });
    // keep only the last unanswered ask card; supersede (hide) the earlier ones
    unansweredAskIds.slice(0, -1).forEach((id) => droppedHitlIds.add(id));

    const effectiveMessages =
      relocatedIds.size || droppedHitlIds.size
        ? working.filter(
            (m) => !relocatedIds.has(m.id) && !droppedHitlIds.has(m.id),
          )
        : working;

    const result: Message[] = [];
    const assistantByUserMessage = new Map<string, Message[]>();
    const processedIds = new Set<string>();

    // First pass: group assistant messages by userMessageId (turn-based grouping)
    effectiveMessages.forEach((msg) => {
      if (msg.type === MessageType.ASSISTANT && msg.userMessageId) {
        const existing = assistantByUserMessage.get(msg.userMessageId) || [];
        existing.push(msg);
        assistantByUserMessage.set(msg.userMessageId, existing);
      }
    });

    // Build set of user message IDs for quick lookup
    const userMessageIds = new Set(
      effectiveMessages
        .filter((m) => m.type === MessageType.USER)
        .map((m) => m.id),
    );

    // Second pass: build reordered list
    effectiveMessages.forEach((msg) => {
      if (processedIds.has(msg.id)) return;

      if (msg.type === MessageType.USER) {
        result.push(msg);
        processedIds.add(msg.id);

        // Get all assistant messages that belong to this user message's turn
        const responses = assistantByUserMessage.get(msg.id) || [];
        responses.forEach((resp) => {
          // Skip if already processed (handles duplicates in source messages)
          if (processedIds.has(resp.id)) return;
          result.push(resp);
          processedIds.add(resp.id);
        });
      } else if (
        msg.type === MessageType.ASSISTANT &&
        msg.userMessageId &&
        userMessageIds.has(msg.userMessageId)
      ) {
        // Skip - will be added after parent user message (grouped by userMessageId)
      } else {
        // Add messages without userMessageId (legacy) or orphaned messages
        result.push(msg);
        processedIds.add(msg.id);
      }
    });

    // Final deduplication check - ensure unique IDs in result
    // Also assign fallback IDs for messages with missing IDs (data issue)
    const seenIds = new Set<string>();
    let fallbackCounter = 0;
    const deduped = result.map((msg, idx) => {
      let id = msg.id;

      // Assign fallback ID if missing
      if (!id) {
        id = `fallback-${msg.type}-${idx}-${++fallbackCounter}`;
        console.warn(
          "[AgentChat] Message missing ID, assigned fallback:",
          id,
          msg.type,
          msg.content?.slice(0, 50),
        );
      }

      // Handle duplicate IDs by appending index
      if (seenIds.has(id)) {
        const originalId = id;
        id = `${id}-dup-${idx}`;
        console.warn(
          "[AgentChat] Duplicate message ID detected:",
          originalId,
          "->",
          id,
          msg.type,
        );
      }

      seenIds.add(id);

      // Return message with guaranteed unique ID
      return msg.id === id ? msg : { ...msg, id };
    });

    // Merge each turn's work messages so phase grouping (in AgentMessage) sees
    // the whole turn and renders identically live and on reload. Gated on the
    // rollout flag so turning phases off restores the exact prior behavior.
    return phaseGroupingEnabled() ? mergeTurnsForPhases(deduped) : deduped;
  }, [messages]);

  return (
    <div
      ref={rootContainerRef}
      className="flex flex-col h-full bg-background text-foreground relative"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag Overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-blue-600/20 backdrop-blur-lg border-4 border-dashed border-blue-400/60 flex items-center justify-center rounded-lg">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-blue-500/30 border-3 border-blue-400/60 mb-6 shadow-2xl animate-bounce">
              <Upload className="w-12 h-12 text-blue-300" strokeWidth={2.5} />
            </div>
            <p className="text-3xl font-bold text-white mb-3 drop-shadow-2xl">
              Drop files here
            </p>
            <p className="text-base text-gray-200 drop-shadow-lg">
              Images, videos, PDFs up to 50MB
            </p>
          </div>
        </div>
      )}

      {/* Messages - cleaner background - only show when chat tab is active */}
      {currentActiveTab === "chat" && (
        <div className="relative flex-1 min-h-0">
          <div
            ref={messagesContainerRef}
            onScroll={handleScroll}
            className="h-full overflow-y-auto overflow-x-hidden bg-background scrollbar-hide"
          >
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center p-8 max-w-md">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <Bot className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">
                    No messages yet
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Start a conversation to get assistance with code, debugging,
                    or planning.
                  </p>
                  {/* Credits display for new session */}
                  {numBalance !== null && !isLoadingCredits && (
                    <>
                      <div className="w-12 h-px bg-border mx-auto mt-4" />
                      <p className="text-sm text-foreground/80 mt-2">
                        Credits remaining: {numBalance.toFixed(2)}
                      </p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-3">
                {reorderedMessages.map((message, index) => {
                  // A loading placeholder is represented by the single bottom
                  // thinking indicator (fixed position). Skipping it inline keeps
                  // the indicator from bobbing between the in-bubble and bottom
                  // positions as the placeholder is dropped/re-added during the
                  // placeholder→real-session handoff. (Activation placeholders
                  // still render — they show the staged progress card.)
                  if (message.isLoading && !message.activation) return null;

                  const prevMessage =
                    index > 0 ? reorderedMessages[index - 1] : null;
                  const isConsecutiveAssistant =
                    message.type === MessageType.ASSISTANT &&
                    prevMessage?.type === MessageType.ASSISTANT &&
                    message.userMessageId === prevMessage.userMessageId;

                  // Does this turn end by handing control back to the user? True
                  // when the next message (same turn) is a HITL ui_payload card
                  // awaiting their input (question, plan approval, etc.). Lets the
                  // phase timeline label the closing node "Over to you" instead of
                  // "Wrap-up". Cheap content guard before the parse.
                  const nextMessage =
                    index < reorderedMessages.length - 1
                      ? reorderedMessages[index + 1]
                      : null;
                  const awaitsUserInput =
                    message.type === MessageType.ASSISTANT &&
                    !!nextMessage &&
                    nextMessage.type === MessageType.ASSISTANT &&
                    nextMessage.content.includes("<ui_payload>") &&
                    (!message.userMessageId ||
                      !nextMessage.userMessageId ||
                      message.userMessageId === nextMessage.userMessageId) &&
                    !!extractUIPayload(nextMessage.content).payload?.hitl_type;

                  return (
                    <div
                      key={message.id}
                      data-message-role={
                        message.type === MessageType.USER ? "user" : "assistant"
                      }
                      className={
                        isConsecutiveAssistant ? "" : index > 0 ? "mt-2" : ""
                      }
                    >
                      <AgentMessage
                        message={message}
                        onOpenFile={onOpenFile}
                        onAction={onAction}
                        showHeader={
                          message.type !== MessageType.ASSISTANT ||
                          index === 0 ||
                          prevMessage?.type !== MessageType.ASSISTANT ||
                          // Show header if responding to different user message (turn-based grouping)
                          !!(
                            message.userMessageId &&
                            prevMessage?.userMessageId &&
                            message.userMessageId !== prevMessage.userMessageId
                          )
                        }
                        workspaceId={workspaceId}
                        previewIframe={previewIframe}
                        projectId={projectId || ""}
                        onEditTheme={onEditTheme}
                        onThemeSaved={onThemeSaved}
                        onBrowseThemes={onBrowseThemes}
                        onConnectionCreated={onConnectionCreated}
                        onCacheTtlSet={onCacheTtlSet}
                        devContainerId={devContainerId}
                        previewUrl={previewUrl}
                        // Reliable live signal: only the last assistant message of
                        // the actively-streaming session is "live" (drives the
                        // shimmer/expanded current phase). Per-message status is
                        // unreliable mid-stream, so we use the session flag here.
                        isStreamingTurn={
                          isStreaming &&
                          index === reorderedMessages.length - 1 &&
                          message.type === MessageType.ASSISTANT
                        }
                        awaitsUserInput={awaitsUserInput}
                      />
                    </div>
                  );
                })}
                {/* The single thinking indicator while waiting for the agent's
                    first output: no messages yet, OR the last message is the
                    user's, OR it's a loading placeholder (rendered here, not
                    inline — see the skip in the map above). One fixed-position
                    indicator across the placeholder→real-session handoff, so it
                    never bobs. Activation placeholders are excluded — they show
                    their own staged progress card inline. */}
                {isStreaming &&
                  (() => {
                    const last =
                      reorderedMessages[reorderedMessages.length - 1];
                    return (
                      reorderedMessages.length === 0 ||
                      last?.type === MessageType.USER ||
                      (last?.isLoading && !last?.activation)
                    );
                  })() && (
                    <div className="p-2.5">
                      <ThinkingIndicator />
                    </div>
                  )}
                {/* Room opened on send so the anchored message can rise to the
                    top; trimmed when the turn ends. */}
                <div
                  ref={anchorSpacerRef}
                  aria-hidden="true"
                  style={{ height: anchorSpacer }}
                />
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Scroll to bottom button */}
          {showScrollToBottom && (
            <button
              type="button"
              onClick={handleScrollToBottom}
              className="absolute bottom-3 right-3 z-10 p-2 rounded-full bg-muted hover:bg-accent border border-border shadow-lg transition-all duration-200"
              aria-label="Scroll to bottom"
            >
              <ArrowDown className="w-4 h-4 text-foreground" />
            </button>
          )}
        </div>
      )}

      {/* Input - cleaner styling */}
      <div className="bg-background p-3 space-y-2">
        {/* Occasional session-feedback pulse (server decides eligibility) */}
        <SessionFeedbackPulse
          sessionId={sessionId ?? null}
          turnCount={messages.filter((m) => m.type === MessageType.USER).length}
          isStreaming={isStreaming}
        />
        {/* Container Setup Indicator - shows during instant resume */}
        {!isContainerReady && (
          <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 dark:border-primary/30 dark:bg-primary/10 px-3 py-1.5 text-xs text-foreground">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
            <span>Setting things up...</span>
          </div>
        )}

        {/* Credits Warning - shows at 80% used, 90% used, or negative balance. */}
        {/* Hidden when BYOC connected (user pays Anthropic directly), and for free-daily */}
        {/* workspaces (they get the CreditDrawer tucked behind the composer instead). */}
        {creditBalance !== null && !freeDaily?.isFreeDaily && (
          <CreditsWarning
            balance={creditBalance}
            allocatedCredits={allocatedCredits ?? undefined}
            onGetCredits={() => navigate("/dashboard/settings/billing")}
            isTyping={prompt.length > 0}
            shownAlerts={shownCreditAlerts}
            byocConnected={byocConnected}
            byocEnabled={byocEnabled}
            onAlertShown={onCreditAlertShown}
          />
        )}

        {/* Email Verification Warning - shows when user needs to verify email */}
        {isEmailVerificationBlocked && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 dark:border-primary/30 dark:bg-primary/10 px-3 py-1.5 text-xs text-foreground">
            <span>
              <span className="text-primary font-medium">
                Email not verified:
              </span>{" "}
              Please verify your email to continue. Check your inbox for the
              verification link.
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onResendVerificationEmail}
                disabled={
                  isResendingVerificationEmail || isCheckingEmailVerification
                }
                className="font-medium rounded-md border border-primary/50 text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors px-2.5 py-1 text-xs whitespace-nowrap flex items-center gap-1.5"
              >
                {isResendingVerificationEmail ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Resend Email"
                )}
              </button>
              <button
                type="button"
                onClick={onCheckEmailVerification}
                disabled={
                  isCheckingEmailVerification || isResendingVerificationEmail
                }
                className="font-medium rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors px-2.5 py-1 text-xs whitespace-nowrap flex items-center gap-1.5"
              >
                {isCheckingEmailVerification ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Checking...
                  </>
                ) : (
                  "I've Verified"
                )}
              </button>
            </div>
          </div>
        )}

        {/* Connection Lost Banner - shows when WebSocket disconnects unexpectedly */}
        {connectionLost && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 dark:border-primary/30 dark:bg-primary/10 px-3 py-1.5 text-xs text-foreground">
            <span>
              <span className="text-primary font-medium">Connection lost.</span>{" "}
              Please refresh and try again.
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onConnectionLostDismiss}
                className="font-medium rounded-md border border-primary/50 text-primary hover:bg-primary/10 transition-colors px-2.5 py-1 text-xs whitespace-nowrap"
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="font-medium rounded-md bg-primary text-white hover:bg-primary/90 transition-colors px-2.5 py-1 text-xs whitespace-nowrap flex items-center gap-1.5"
              >
                <RefreshCw className="w-3 h-3" />
                Refresh
              </button>
            </div>
          </div>
        )}

        {/* Viewer Mode Warning - shows when user is a viewer */}
        {isViewer && (
          <div className="flex items-center gap-3 rounded-lg border border-muted bg-muted/50 px-3 py-1.5 text-xs text-muted-foreground">
            <Eye className="w-4 h-4 flex-shrink-0" />
            <span>
              <span className="font-medium">View only:</span> You have read-only
              access to this workspace. Contact an admin to request edit
              permissions.
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Free-tier credit drawer — a rounded tray tucked BEHIND the composer.
              Only renders for standalone free workspaces that have hit a wall; the
              composer below (opaque bg-card, z-10) occludes its lower edge so only
              the notice peeks above. Upgrade Now opens the same modal as send. */}
          {freeDaily?.isFreeDaily && !creditDrawerDismissed && (
            <CreditDrawer
              freeDaily={freeDaily}
              onUpgrade={() => onOpenUpgrade?.()}
              onDismiss={() => setCreditDrawerDismissed(true)}
            />
          )}
          <div
            data-tour-anchor="composer"
            className={cn(
              "relative z-10 flex flex-col p-3 gap-2 bg-card focus-within:border-border/50",
              // Desktop: full rounded border
              !isMobile && "border border-border/50 rounded-2xl",
              // Mobile: rounded border, with safe area padding at bottom
              isMobile &&
                "border border-border/50 rounded-2xl pb-[env(safe-area-inset-bottom)]",
              isChatBlocked && "opacity-50 pointer-events-none blur-[2px]",
            )}
          >
            {/* Tests reopen tab — flush on the top-right edge of the input box,
                shown after the test banner is dismissed. Suppressed while the credit
                drawer is up so the drawer stays the top-most notice (no overlap). */}
            {!(freeDaily?.isFreeDaily && freeDaily.wall !== "none" && !creditDrawerDismissed) && (
              <PreviewTestReopenTab />
            )}

            {/* Attached Files Preview - Compact thumbnails */}
            {files.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {files.map((fileWithState) => {
                  const preview = filePreviews.get(fileWithState.file.name);
                  const isUploading =
                    fileWithState.uploadState === "uploading" ||
                    fileWithState.uploadState === "pending";
                  const isFailed = fileWithState.uploadState === "failed";

                  return (
                    <div
                      key={fileWithState.file.name}
                      className="relative group bg-muted rounded-xl overflow-visible transition-colors"
                      style={{ width: "56px", height: "56px" }}
                    >
                      {/* Image preview or file icon */}
                      {preview ? (
                        <img
                          src={preview}
                          alt={fileWithState.file.name}
                          className={cn(
                            "w-full h-full object-cover",
                            isUploading && "opacity-50",
                          )}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-muted rounded-xl">
                          {fileWithState.file.type?.includes("pdf") ? (
                            <FileText
                              className={cn(
                                "w-6 h-6 text-muted-foreground",
                                isUploading && "opacity-50",
                              )}
                            />
                          ) : fileWithState.file.type?.includes("video") ? (
                            <FileIcon
                              className={cn(
                                "w-6 h-6 text-muted-foreground",
                                isUploading && "opacity-50",
                              )}
                            />
                          ) : (
                            <FileIcon
                              className={cn(
                                "w-6 h-6 text-muted-foreground",
                                isUploading && "opacity-50",
                              )}
                            />
                          )}
                        </div>
                      )}

                      {/* Upload progress overlay */}
                      {isUploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-xl">
                          <Loader2 className="w-4 h-4 animate-spin text-white" />
                        </div>
                      )}

                      {/* Filename — gradient scrim caption */}
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-center justify-end rounded-b-xl bg-gradient-to-t from-black/60 via-black/20 to-transparent px-0.5 pb-0.5 pt-4 opacity-0 transition-opacity group-hover:opacity-100">
                        <span className="w-full truncate px-0.5 text-center text-[8px] leading-tight text-white/90">
                          {fileWithState.file.name}
                        </span>
                        {isFailed && (
                          <span className="text-[8px] text-destructive">
                            Failed
                          </span>
                        )}
                      </div>

                      {/* Remove button (only when not uploading) */}
                      {onRemoveAttachment && !isUploading && (
                        <button
                          type="button"
                          onClick={() =>
                            onRemoveAttachment(fileWithState.file.name)
                          }
                          className="absolute -top-1.5 -right-1.5 z-10 rounded-full bg-destructive p-0.5 text-destructive-foreground opacity-0 transition-opacity hover:bg-destructive/90 group-hover:opacity-100"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* @ Mention dropdown (All/Files/Projects tabs - Lovable-style) */}
            <MentionDropdown
              isOpen={showMentionDropdown}
              query={mentionQuery}
              selectedIndex={selectedMentionIndex}
              onIndexChange={setSelectedMentionIndex}
              onSelect={handleMentionSelect}
              confirmSelectionRef={mentionConfirmRef}
              switchTabRef={mentionSwitchTabRef}
              itemCountRef={mentionItemCountRef}
              files={mentionFiles}
              projects={referenceableProjects}
              themes={themes}
              currentTheme={currentTheme}
              isLoadingProjects={isLoadingProjects}
            />

            {/* Skill picker is now embedded in AttachmentDropdown as a submenu */}

            {/* Mention chips (projects, files, themes, skills) */}
            {mentionChips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 px-2 pb-1">
                {mentionChips.map((chip, idx) => (
                  <MentionBadge
                    key={`${chip.type}-${chip.id || chip.name}`}
                    type={chip.type}
                    name={chip.name}
                    id={chip.id}
                    onRemove={() => handleRemoveMentionChip(idx)}
                  />
                ))}
              </div>
            )}

            {/* Textbox with inline voice button on mobile */}
            <div className="flex items-start gap-2">
              {/* URL link styling: when prompt contains URLs, a styled mirror
                  div renders behind the textarea showing URLs as link-style
                  (primary + underline) text. Textarea text becomes transparent
                  but caret + selection stay visible. Mirror and textarea must
                  share IDENTICAL font / size / line-height / padding / wrap
                  rules — any divergence causes the highlight to drift from
                  the caret. The shared className constant locks them together. */}
              {(() => {
                // Use `overflow-wrap:anywhere` (not `break-words`) so a long
                // space-free token — e.g. a CDN URL — breaks at the SAME
                // character in both the textarea and the mirror div. With
                // `break-word` the two layers pick different break points, which
                // drifts the blue link off the caret and lets the URL overflow
                // the box. `anywhere` is deterministic, keeping them aligned.
                const sharedTextStyles =
                  "min-h-[44px] max-h-[200px] py-2 px-2 text-sm leading-[1.5] font-sans whitespace-pre-wrap [overflow-wrap:anywhere] overflow-y-auto";
                return (
                  <div className="relative flex-1">
                    {hasUrl && (
                      <div
                        ref={mirrorRef}
                        aria-hidden="true"
                        className={cn(
                          "absolute inset-0 pointer-events-none",
                          sharedTextStyles,
                          "text-foreground",
                        )}
                      >
                        {promptSegments.map((seg, i) =>
                          seg.type === "url" ? (
                            <a
                              key={i}
                              href={seg.value}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-primary underline decoration-primary/50 underline-offset-2 hover:decoration-primary cursor-pointer pointer-events-auto"
                              title={`Open ${seg.value} in a new tab`}
                            >
                              {seg.value}
                            </a>
                          ) : (
                            <span key={i}>{seg.value}</span>
                          ),
                        )}
                        {/* Trailing space preserves height when prompt ends in newline */}
                        {prompt.endsWith("\n") && " "}
                      </div>
                    )}
                    <Textarea
                      ref={textareaRef}
                      // Stable E2E hook — the placeholder below is state-dependent
                      // (streaming / attachments) and collides with the visual
                      // editor's FloatingToolbar input, which is always mounted.
                      data-testid="agent-composer"
                      value={prompt}
                      onChange={handlePromptChange}
                      onKeyDown={handleKeyDown}
                      onScroll={(e) => {
                        // Keep the link mirror locked to the textarea's scroll
                        // position so links stay aligned with the caret once the
                        // input scrolls past its max height.
                        if (mirrorRef.current) {
                          mirrorRef.current.scrollTop = e.currentTarget.scrollTop;
                          mirrorRef.current.scrollLeft =
                            e.currentTarget.scrollLeft;
                        }
                      }}
                      placeholder={
                        isStreaming
                          ? "Stop to send another message..."
                          : files.length > 0
                            ? `What would you like to do with ${files.length > 1 ? "these files" : "this file"}?`
                            : "Ask OptiDev..."
                      }
                      disabled={disabled}
                      className={cn(
                        "block w-full resize-none",
                        sharedTextStyles,
                        "bg-transparent border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                        "placeholder:text-muted-foreground/60",
                        // When mirror renders, hide textarea text but keep caret + selection visible
                        hasUrl
                          ? "text-transparent caret-foreground selection:bg-primary/30 selection:text-foreground"
                          : "text-foreground",
                      )}
                      rows={1}
                    />
                  </div>
                );
              })()}

              {/* Voice/Send/Stop button - inline with textarea on mobile */}
              {isMobile &&
                (isStreaming ? (
                  // Stop button when streaming - matches desktop pattern
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      if (onStopChat) {
                        onStopChat();
                      }
                    }}
                    className="h-8 w-8 rounded-full flex-shrink-0 hover:bg-destructive/10 stop-button-ring"
                    title="Stop generation"
                  >
                    <StopCircle className="w-4 h-4 text-destructive" />
                  </Button>
                ) : prompt.trim() || files.length > 0 ? (
                  // Send button when there's content
                  <Button
                    type="submit"
                    size="icon"
                    variant="ghost"
                    disabled={disabled || isChatBlocked || !isContainerReady}
                    className="h-8 w-8 rounded-full flex-shrink-0 bg-primary hover:bg-primary/90 text-primary-foreground"
                    title={!isContainerReady ? "Setting up..." : "Send message"}
                  >
                    <ArrowUp className="w-4 h-4" strokeWidth={2.5} />
                  </Button>
                ) : (
                  // Mic button when idle and no content
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    disabled={disabled || isProcessingVoice || isChatBlocked}
                    onClick={handleVoiceToggle}
                    className={cn(
                      "h-7 w-7 rounded-md flex-shrink-0 border border-border bg-background dark:bg-muted/40 hover:bg-muted/80 hover:border-border transition-colors",
                      isRecording &&
                        "text-destructive animate-pulse border-destructive/60",
                    )}
                    title={isRecording ? "Stop recording" : "Start voice input"}
                  >
                    {isProcessingVoice ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Mic
                        className={cn("w-4 h-4", isRecording && "fill-current")}
                      />
                    )}
                  </Button>
                ))}
            </div>

            {/* Controls row below textbox */}
            {isMobile ? (
              /* Mobile: + | Chat | Preview | ... */
              <div className="flex items-center gap-2 pt-1 pb-2">
                {/* Left: Attachment dropdown (image gen, attach files, etc.) */}
                <div className="flex-shrink-0">
                  <AttachmentDropdown
                    onSelectAttach={onSelectAttach}
                    onSelectConnections={onSelectConnections}
                    onSelectProjectSettings={onSelectProjectSettings}
                    onSelectImageGenerator={onSelectImageGenerator}
                    onSelectMediaLibrary={onSelectMediaLibrary}
                    onSelectOptisignsAssets={onSelectOptisignsAssets}
                    onSelectThemes={onSelectThemes}
                    onSelectAddReference={handleAddReference}
                    onSelectSkill={(skill) => {
                      setMentionChips((prev) => {
                        const exists = prev.some(
                          (c) => c.type === "skill" && c.id === skill.id,
                        );
                        if (exists) return prev;
                        return [
                          ...prev,
                          { type: "skill", name: skill.name, id: skill.id },
                        ];
                      });
                    }}
                    selectedSkillIds={mentionChips
                      .filter((c) => c.type === "skill" && c.id)
                      .map((c) => c.id!)}
                    disabled={isStreaming || disabled}
                    isMobile={isMobile}
                  />
                </div>

                {/* Center: Chat | Preview tabs */}
                <div className="flex-1 flex items-center justify-center">
                  <div className="flex items-center bg-muted rounded-xl p-1">
                    <button
                      type="button"
                      onClick={() => onMobileActiveTabChange?.("chat")}
                      className={cn(
                        "flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                        currentActiveTab === "chat"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <MessageCircle className="w-4 h-4" />
                      Chat
                    </button>
                    <button
                      type="button"
                      onClick={() => onMobileActiveTabChange?.("preview")}
                      className={cn(
                        "flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                        currentActiveTab === "preview"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <Eye className="w-4 h-4" />
                      Preview
                    </button>
                  </div>
                </div>

                {/* Right: More options button */}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onOpenSettings}
                  className="h-9 w-9 rounded-xl bg-muted flex-shrink-0"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              /* Desktop: Original layout */
              <div className="flex items-center gap-1.5 justify-between">
                {/* Left side controls */}
                <div className="flex items-center gap-1.5">
                  {/* Attachment dropdown button */}
                  <AttachmentDropdown
                    onSelectAttach={onSelectAttach}
                    onSelectConnections={onSelectConnections}
                    onSelectProjectSettings={onSelectProjectSettings}
                    onSelectImageGenerator={onSelectImageGenerator}
                    onSelectMediaLibrary={onSelectMediaLibrary}
                    onSelectOptisignsAssets={onSelectOptisignsAssets}
                    onSelectThemes={onSelectThemes}
                    onSelectAddReference={handleAddReference}
                    onSelectSkill={(skill) => {
                      setMentionChips((prev) => {
                        const exists = prev.some(
                          (c) => c.type === "skill" && c.id === skill.id,
                        );
                        if (exists) return prev;
                        return [
                          ...prev,
                          { type: "skill", name: skill.name, id: skill.id },
                        ];
                      });
                    }}
                    selectedSkillIds={mentionChips
                      .filter((c) => c.type === "skill" && c.id)
                      .map((c) => c.id!)}
                    disabled={isStreaming || disabled}
                    isMobile={isMobile}
                  />

                  <AgentModeDropdown
                    mode={mode}
                    onChange={onModeChange}
                    disabled={isStreaming}
                    compact={isCompactToolbar}
                  />

                  <ModelDropdown
                    model={model}
                    onChange={onModelChange}
                    disabled={isStreaming}
                    compact={isCompactToolbar}
                  />
                </div>

                {/* Right side controls */}
                <div className="flex items-center gap-1.5">
                  {/* Voice input button */}
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    disabled={
                      disabled ||
                      isStreaming ||
                      isProcessingVoice ||
                      isChatBlocked
                    }
                    onClick={handleVoiceToggle}
                    className={cn(
                      "h-7 w-7 rounded-md border border-border bg-background dark:bg-muted/40 hover:bg-muted/80 hover:border-border transition-colors",
                      isRecording &&
                        "text-destructive animate-pulse border-destructive/60",
                    )}
                    title={isRecording ? "Stop recording" : "Start voice input"}
                  >
                    {isProcessingVoice ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Mic
                        className={cn("w-4 h-4", isRecording && "fill-current")}
                      />
                    )}
                  </Button>

                  {/* Send/Stop button - single button to prevent click bleed-through on state change */}
                  <Button
                    type="button"
                    aria-label={isStreaming ? "Stop generation" : "Send message"}
                    disabled={
                      !isStreaming &&
                      (!prompt.trim() ||
                        disabled ||
                        !isContainerReady ||
                        files.some(
                          (f) =>
                            f.uploadState === "uploading" ||
                            f.uploadState === "pending",
                        ))
                    }
                    onClick={() => {
                      if (isStreaming && onStopChat) {
                        onStopChat();
                      } else if (
                        prompt.trim() &&
                        isInsufficientCredits &&
                        !byocConnected
                      ) {
                        // Out of credits → open the upgrade modal instead of sending;
                        // the typed prompt stays in the box (matches handleSubmit).
                        onOpenUpgrade?.();
                      } else if (
                        prompt.trim() &&
                        !disabled &&
                        !isStreaming &&
                        isContainerReady
                      ) {
                        const mentionPrefix = mentionChips
                          .map((c) => {
                            if (c.type === "project")
                              return `@project:${c.name}[${c.id}]`;
                            if (c.type === "file") return `@file:${c.name}`;
                            if (c.type === "skill")
                              return `@skill:${c.id || c.name}`;
                            if (c.type === "asset")
                              return `@asset:${c.id}[${c.name}]`;
                            return `@theme:${c.name}`;
                          })
                          .join(" ");
                        const fullPrompt = mentionPrefix
                          ? `${mentionPrefix} ${prompt.trim()}`
                          : prompt.trim();
                        onSendPrompt(fullPrompt);
                        setPrompt("");
                        setMentionChips([]);
                        // Reset textarea height to original size (min-h-[44px] from CSS will take over)
                        if (textareaRef.current) {
                          textareaRef.current.style.height = "auto";
                        }
                        // Pin the sent message to the top and open room for
                        // the reply.
                        beginAnchoredTurn();
                      }
                    }}
                    size="icon"
                    variant={isStreaming ? "ghost" : "default"}
                    className={cn(
                      "h-7 w-7 rounded-md transition-colors",
                      isStreaming
                        ? "rounded-full hover:bg-destructive/10 stop-button-ring"
                        : prompt.trim() || files.length > 0
                          ? "bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
                          : "bg-muted hover:bg-accent text-foreground disabled:opacity-50",
                    )}
                  >
                    {isStreaming ? (
                      <StopCircle className="w-4 h-4 text-destructive" />
                    ) : (
                      <ArrowUp className="w-4 h-4" strokeWidth={2.5} />
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Voice Recording Dialog */}
      <Dialog
        open={isRecording}
        onOpenChange={(open) => {
          if (!open) stopRecording();
        }}
      >
        <DialogContent
          className="max-w-[90vw] sm:max-w-md mx-auto rounded-3xl"
          hideCloseButton
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            stopRecording();
          }}
        >
          <div className="flex flex-col items-center justify-center py-6 sm:py-8 px-4 sm:px-6">
            {/* Animated Microphone Icon */}
            <div className="relative mb-4 sm:mb-6">
              <div className="absolute inset-0 bg-purple-500/20 rounded-full animate-ping" />
              <div className="relative bg-purple-600 text-white p-5 sm:p-6 rounded-full">
                <Mic className="w-10 h-10 sm:w-12 sm:h-12" />
              </div>
            </div>

            {/* Listening Text */}
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              Listening...
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center max-w-xs px-2">
              Speak clearly into your microphone
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
